import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { ShieldAlert, ShieldCheck, X } from "lucide-react";

/**
 * Compact banner that surfaces geolocation-based anomalies on login:
 *  - new IP address never seen before
 *  - access from a country never seen before (high severity)
 *
 * Uses sessionStorage so the banner shows once per browser session
 * (dismissal doesn't stick across new logins from suspicious locations).
 */
export default function SecurityBanner() {
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/security/status")
      .then((r) => {
        if (!cancelled) setData(r.data);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!data || dismissed) return null;
  const alerts = data.alerts || [];
  if (alerts.length === 0) return null;

  const hasNewCountry = alerts.some((a) => a.type === "new_country");
  const tone = hasNewCountry ? "high" : "medium";

  const dismiss = () => {
    setDismissed(true);
    sessionStorage.setItem("crm:security-banner-dismissed", "1");
  };

  // Suppress if already dismissed this session
  if (sessionStorage.getItem("crm:security-banner-dismissed") === "1") return null;

  return (
    <Card
      data-testid="security-banner"
      className={`mb-6 border-0 rounded-2xl overflow-hidden ${
        tone === "high" ? "bg-red-950/40 border border-red-900/60" : "bg-amber-950/40 border border-amber-900/60"
      }`}
    >
      <div className="p-4 flex items-start gap-3">
        <div className={`w-9 h-9 rounded-full shrink-0 flex items-center justify-center ${
          tone === "high" ? "bg-red-900/40 text-red-300" : "bg-amber-900/40 text-amber-300"
        }`}>
          <ShieldAlert className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className={`text-sm font-semibold mb-1 ${tone === "high" ? "text-red-200" : "text-amber-200"}`}>
            {tone === "high" ? "Accesso da nuova localizzazione" : "Nuovo indirizzo di accesso"}
          </div>
          <ul className="text-xs space-y-0.5 text-stone-300/90">
            {alerts.map((a, i) => (
              <li key={`${a.type}-${i}`} className="flex items-center gap-1.5">
                <span className={`inline-block w-1 h-1 rounded-full ${
                  a.severity === "high" ? "bg-red-400" : "bg-amber-400"
                }`} />
                {a.message}
              </li>
            ))}
          </ul>
          <div className="mt-2 text-[11px] text-stone-400">
            IP: <span className="font-mono">{data.current?.ip || "—"}</span>
            {data.current?.city && <> · {data.current.city}</>}
            {data.current?.country && <> · <span className="font-medium">{data.current.country}</span></>}
            {" · "}
            <a href="/accessi" className="text-[#F56A1F] hover:underline">Vedi cronologia accessi →</a>
          </div>
        </div>
        <button
          data-testid="security-banner-dismiss"
          onClick={dismiss}
          className="text-stone-400 hover:text-stone-100 shrink-0 p-1"
          aria-label="Chiudi"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </Card>
  );
}

export function SecurityOkBadge() {
  return (
    <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-emerald-400/80 font-semibold">
      <ShieldCheck className="w-3 h-3" /> Accesso sicuro
    </div>
  );
}
